<script setup lang="ts">
/* =============================================================
   FIND A BERTH
   =============================================================
   §4 of the brief, the main part of the demo: pick a place, a
   ship, a stay and what the ship needs, and see every berth
   around on a map and in a list -- with price, availability and
   fit for that ship already worked out, best first.

   Nothing is hidden by default. Berths that do not suit stay in
   the list, greyed, with the reason ("Too long — takes up to
   110 m"), because "why not that one?" is exactly what someone
   comparing berths asks. A toggle hides them.

   Tick up to four to compare side by side.
   ============================================================= */

import { computed, nextTick, onMounted, ref, watch } from 'vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import MapView from '@/components/MapView.vue'
import SearchControls from '@/components/search/SearchControls.vue'
import BerthResultCard from '@/components/search/BerthResultCard.vue'
import { to } from '@/config/routes'
import { listCities, searchBerths } from '@/lib/search'
import { listVessels } from '@/lib/vessels'
import { errorMessage } from '@/lib/errors'
import { useSearchContext } from '@/lib/useSearchContext'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { rankResults, resultStatus, shortPrice } from '@/lib/searchLabels'
import type { MapPoint } from '@/lib/map'
import type { BerthCity, BerthResult, Vessel } from '@/lib/types'

useDocumentTitle('Find a berth')

const { context, amenities, hasStay, update } = useSearchContext()
const { user } = useAuth()
const companyId = () => user.value?.companyId ?? 0

const cities = ref<BerthCity[]>([])
const vessels = ref<Vessel[]>([])
const results = ref<BerthResult[]>([])
const loading = ref(false)
const error = ref('')
const hideUnsuitable = ref(false)
const selectedId = ref<number | null>(null)
const compareIds = ref<number[]>([])

const MAX_COMPARE = 4
const RADIUS_KM = 25
/** The opening map view frames berths this close to the place searched for. */
const FRAME_KM = 2

const city = computed(() => cities.value.find((c) => c.city === context.value.city) ?? null)
const vessel = computed(() => vessels.value.find((v) => v.id === context.value.vessel) ?? null)

onMounted(async () => {
  try {
    const [c, v] = await Promise.all([listCities(), listVessels(companyId())])
    cities.value = c
    vessels.value = v
  } catch (e) {
    error.value = errorMessage(e, 'Could not load the search.')
  }
})

/* --- searching ------------------------------------------------------------ */

let run = 0

async function search(): Promise<void> {
  const place = city.value
  if (!place) {
    results.value = []
    return
  }
  const mine = ++run
  loading.value = true
  error.value = ''
  try {
    const rows = await searchBerths({
      lat: place.latitude,
      lng: place.longitude,
      radiusKm: RADIUS_KM,
      vessel: vessel.value,
      amenities: amenities.value,
      arrival: hasStay.value ? context.value.from : null,
      departure: hasStay.value ? context.value.to : null,
    })
    // A slower, older search must not overwrite a newer one.
    if (mine !== run) return
    results.value = rows
    // Keep only comparisons that are still in the result set.
    compareIds.value = compareIds.value.filter((id) => rows.some((r) => r.id === id))
  } catch (e) {
    if (mine === run) error.value = errorMessage(e, 'Could not search berths.')
  } finally {
    if (mine === run) loading.value = false
  }
}

watch([city, vessel, () => context.value.from, () => context.value.to, amenities], search, { deep: true })

/* --- what is shown --------------------------------------------------------- */

const ranked = computed(() => rankResults(results.value, hasStay.value))

const isSuitable = (r: BerthResult) => (hasStay.value ? resultStatus(r, true).bookable : r.fits)

const shown = computed(() => (hideUnsuitable.value ? ranked.value.filter(isSuitable) : ranked.value))

const suitableCount = computed(() => results.value.filter(isSuitable).length)

const summary = computed(() => {
  const n = results.value.length
  if (n === 0) return ''
  const place = city.value?.city ?? ''
  const fit = vessel.value || hasStay.value ? ` · ${suitableCount.value} suitable` : ''
  return `${n} berth${n === 1 ? '' : 's'} within ${RADIUS_KM} km of ${place}${fit}`
})

const points = computed<MapPoint[]>(() =>
  shown.value.map((r) => ({
    id: r.id,
    lat: r.latitude,
    lng: r.longitude,
    title: r.name,
    subtitle: resultStatus(r, hasStay.value).label,
    label: hasStay.value ? shortPrice(r.price_eur) : undefined,
    tone: r.id === selectedId.value ? 'selected' : isSuitable(r) ? 'default' : 'muted',
    // Open on the place itself; neighbours further out are a zoom away.
    frame: r.distance_km <= FRAME_KM,
  })),
)

/* --- selection and comparison --------------------------------------------- */

async function selectFromMap(point: MapPoint): Promise<void> {
  selectedId.value = Number(point.id)
  await nextTick()
  document
    .querySelector(`[data-berth="${point.id}"]`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
}

function toggleCompare(id: number): void {
  compareIds.value = compareIds.value.includes(id)
    ? compareIds.value.filter((x) => x !== id)
    : [...compareIds.value, id].slice(0, MAX_COMPARE)
}

const compareLink = computed(() => to.compare(compareIds.value, context.value))
</script>

<template>
  <PageHeader
    title="Find a berth"
    description="Pick a place, your ship and your stay. Berths that do not suit stay visible, with the reason."
  />

  <Panel class="controlsPanel">
    <SearchControls :context="context" :vessels="vessels" :cities="cities" @update="update" />
  </Panel>

  <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

  <Panel v-if="!context.city">
    <EmptyState
      icon="search"
      title="Where do you need to dock?"
      description="Choose a place above, or start with one of these."
    >
      <template #action>
        <div class="quickCities">
          <Button
            v-for="c in cities.slice(0, 6)"
            :key="c.city"
            variant="secondary"
            size="sm"
            icon="pin"
            @click="update({ city: c.city })"
          >
            {{ c.city }}
          </Button>
        </div>
      </template>
    </EmptyState>
  </Panel>

  <div v-else class="results">
    <section class="list" aria-label="Results">
      <div class="listHead">
        <p class="summary">{{ loading && results.length === 0 ? 'Searching…' : summary }}</p>
        <label v-if="results.length" class="toggle">
          <input v-model="hideUnsuitable" type="checkbox" />
          Hide unsuitable
        </label>
      </div>

      <p v-if="!hasStay && results.length" class="hint">
        Add arrival and departure to see prices and availability.
      </p>

      <p v-if="!loading && results.length === 0" class="panel-status">No berths within {{ RADIUS_KM }} km.</p>

      <div class="cards">
        <BerthResultCard
          v-for="r in shown"
          :key="r.id"
          :result="r"
          :has-stay="hasStay"
          :selected="r.id === selectedId"
          :compared="compareIds.includes(r.id)"
          :compare-disabled="compareIds.length >= MAX_COMPARE"
          :to="to.berthProfile(r.id, context)"
          @select="selectedId = r.id"
          @toggle-compare="toggleCompare(r.id)"
        />
      </div>
    </section>

    <section class="mapCol" aria-label="Map">
      <MapView :points="points" height="100%" wheel-zoom class="map" @select="selectFromMap" />
    </section>
  </div>

  <div v-if="compareIds.length > 0" class="compareBar" role="region" aria-label="Comparison">
    <span class="compareText">
      {{ compareIds.length }} selected
      <span class="compareHint">{{ compareIds.length < 2 ? '— pick at least one more' : '' }}</span>
    </span>
    <div class="compareActions">
      <Button variant="ghost" size="sm" @click="compareIds = []">Clear</Button>
      <Button size="sm" icon="layers" :to="compareIds.length >= 2 ? compareLink : undefined" :disabled="compareIds.length < 2">
        Compare
      </Button>
    </div>
  </div>
</template>

<style scoped>
.controlsPanel {
  margin-bottom: var(--space-md);
}

.quickCities {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--space-2xs);
}

.results {
  display: grid;
  grid-template-columns: minmax(20rem, 28rem) minmax(0, 1fr);
  gap: var(--space-md);
  align-items: start;
}

.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  min-width: 0;
}

.listHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
}

.summary {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  flex: none;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  cursor: pointer;
}

.toggle input {
  accent-color: var(--color-accent);
}

.hint {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
}

.cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  /* Room for the compare bar so the last card is never under it. */
  padding-bottom: var(--space-2xl);
}

/* The map stays in view while the list scrolls past it. */
.mapCol {
  position: sticky;
  top: calc(var(--topbar-height) + var(--space-sm));
  height: calc(100dvh - var(--topbar-height) - var(--space-xl));
  min-height: 24rem;
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.compareBar {
  position: fixed;
  inset-inline: 0;
  bottom: var(--space-md);
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  width: min(30rem, calc(100vw - 2 * var(--space-md)));
  margin-inline: auto;
  padding: var(--space-2xs) var(--space-2xs) var(--space-2xs) var(--space-md);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-lg);
}

.compareText {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
}

.compareHint {
  font-weight: var(--weight-regular, 400);
  color: var(--color-text-muted);
}

.compareActions {
  display: flex;
  gap: var(--space-3xs);
}

/* Narrow: the map goes on top, short, and the list follows it. */
@media (max-width: 62rem) {
  .results {
    grid-template-columns: minmax(0, 1fr);
  }

  .mapCol {
    position: static;
    order: -1;
    height: 18rem;
    min-height: 0;
  }
}
</style>
